import { useEffect, useState } from 'react';
import Modal from '@/components/Modal.jsx';
import DataTable from '@/components/DataTable.jsx';
import { api, ApiError } from '@/lib/api.js';
import { round2 } from '@/lib/format.js';

const num = (key, label, title) => ({ key, label, title: title ?? label, numeric: true });

/* Opponent, venue and final score, as the old cell template rendered it. */
const fixtureColumn = {
	key: 'opponent_team_name',
	label: 'Fixture',
	title: 'Opponent, venue and final score',
	render: (opponent, row) => {
		if (!opponent) return '—';
		const venue = row.was_home ? 'H' : 'A';
		const score =
			row.team_h_score == null || row.team_a_score == null
				? ''
				: ` ${row.team_h_score}–${row.team_a_score}`;
		return `${opponent} (${venue})${score}`;
	},
};

const playerColumns = [
	{ ...num('round', 'GW', 'Gameweek'), defaultSort: 'asc' },
	fixtureColumn,
	num('total_points', 'Pts', 'Points'),
	num('minutes', 'MP', 'Minutes played'),
	num('goals_scored', 'G', 'Goals scored'),
	num('assists', 'A', 'Assists'),
	num('clean_sheets', 'CS', 'Clean sheets'),
	{ ...num('expected_goal_involvements', 'xGI', 'Expected goal involvements'), render: round2 },
	{ ...num('expected_goals', 'xG', 'Expected goals'), render: round2 },
	{ ...num('expected_assists', 'xA', 'Expected assists'), render: round2 },
	{ ...num('expected_goals_conceded', 'xGC', 'Expected goals conceded'), render: round2 },
	num('goals_conceded', 'GC', 'Goals conceded'),
	num('own_goals', 'OG', 'Own goals'),
	num('penalties_saved', 'PS', 'Penalties saved'),
	num('penalties_missed', 'PM', 'Penalties missed'),
	num('yellow_cards', 'YC', 'Yellow cards'),
	num('red_cards', 'RC', 'Red cards'),
	num('saves', 'S', 'Saves'),
	num('bonus', 'B', 'Bonus'),
	num('bps', 'BPS', 'Bonus points system'),
	num('ict_index', 'ICT', 'ICT index'),
	{ ...num('value', 'Cost', 'Cost'), render: (v) => `£${v / 10}` },
	{
		...num('transfers_in', 'TX In', 'Transfers in'),
		render: (v) => Number(v ?? 0).toLocaleString('en-GB'),
	},
	{
		...num('transfers_out', 'TX Out', 'Transfers out'),
		render: (v) => Number(v ?? 0).toLocaleString('en-GB'),
	},
];

const teamColumns = [
	{ ...num('gw', 'GW', 'Gameweek'), defaultSort: 'asc' },
	{
		key: 'fixtures',
		label: 'Fixture',
		title: 'Fixtures played this gameweek',
		sortable: false,
		render: (fixtures) =>
			(fixtures ?? [])
				.map((f) => `${f.home} ${f.home_score}–${f.away_score} ${f.away}`)
				.join(', ') || '—',
	},
	num('total_points', 'Pts', 'Points'),
	{ ...num('xG', 'xG', 'Expected goals'), render: round2 },
	{ ...num('xGI', 'xGI', 'Expected goal involvements'), render: round2 },
	{ ...num('xA', 'xA', 'Expected assists'), render: round2 },
	{ ...num('xGC', 'xGC', 'Expected goals conceded'), render: round2 },
	num('goals', 'G', 'Goals scored'),
	num('assists', 'A', 'Assists'),
	num('clean_sheets', 'CS', 'Clean sheets'),
	num('goals_conceded', 'GC', 'Goals conceded'),
	num('own_goals', 'OG', 'Own goals'),
	num('penalties_saved', 'PS', 'Penalties saved'),
	num('penalties_missed', 'PM', 'Penalties missed'),
	num('yellow_cards', 'YC', 'Yellow cards'),
	num('red_cards', 'RC', 'Red cards'),
];

/*
 * Gameweek-by-gameweek breakdown for one player or team in one season.
 *
 * `kind` picks the endpoint and the column set:
 *   player -> /api/history/season/<season>?player=<id>, which nests the
 *             rows under `history`
 *   team   -> /api/history/season/<season>?team=<id>, which returns them
 *             directly
 */
export default function GwBreakdownModal({ kind, season, id, name, onClose }) {
	const [rows, setRows] = useState([]);
	const [status, setStatus] = useState('loading');
	const [error, setError] = useState(null);

	useEffect(() => {
		let cancelled = false;
		const query = kind === 'team' ? `team=${id}` : `player=${id}`;
		api
			.get(`/api/history/season/${season}?${query}`)
			.then((payload) => {
				if (cancelled) return;
				const list = kind === 'team' ? payload : payload?.history;
				setRows(Array.isArray(list) ? list : []);
				setStatus('ready');
			})
			.catch((err) => {
				if (cancelled) return;
				setError(err instanceof ApiError ? err.message : 'Could not load the breakdown.');
				setStatus('error');
			});
		return () => {
			cancelled = true;
		};
	}, [kind, season, id]);

	return (
		<Modal title={`${season} gameweek breakdown — ${name}`} onClose={onClose} wide>
			{status === 'loading' && (
				<div className="tab-loading" role="status">
					<span className="spinner" aria-hidden="true" />
					<span>Loading breakdown…</span>
				</div>
			)}
			{status === 'error' && (
				<div className="notice notice--error" role="alert">
					<strong>Could not load the breakdown.</strong>
					<span>{error}</span>
				</div>
			)}
			{status === 'ready' && (
				<DataTable
					columns={kind === 'team' ? teamColumns : playerColumns}
					rows={rows}
					rowKey={(r, i) => `${r.round ?? r.gw}-${i}`}
					filterable={false}
					emptyMessage="No gameweek data recorded."
				/>
			)}
		</Modal>
	);
}
